import { FormEvent, useState } from 'react';
import { FamilyRole } from '@kinapp/shared';
import { api } from '../lib/api';
import { FormField } from './FormField';

interface CreateInviteFormProps {
  familyId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function CreateInviteForm({ familyId, onSuccess, onCancel }: CreateInviteFormProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<FamilyRole>(FamilyRole.MEMBER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await api.createInvite({
        familyId,
        email: email.trim(),
        role,
      });

      // Show invite link
      setInviteToken(response.token);
    } catch (err: any) {
      setError(err.message || 'Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!inviteToken) return;

    const inviteLink = `${window.location.origin}/invite/${inviteToken}`;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Success state - show invite link
  if (inviteToken) {
    const inviteLink = `${window.location.origin}/invite/${inviteToken}`;

    return (
      <div>
        <div className="rounded-md bg-green-50 p-4 mb-4">
          <p className="text-sm text-green-800 font-medium mb-2">Invite created successfully!</p>
          <p className="text-sm text-green-700">
            Share this link with <strong>{email}</strong>:
          </p>
        </div>

        <div className="mb-6">
          <label className="label">Invite link</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={inviteLink}
              readOnly
              className="input flex-1 bg-gray-50"
              onClick={(e) => e.currentTarget.select()}
            />
            <button
              onClick={copyToClipboard}
              className="btn btn-secondary whitespace-nowrap"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <button onClick={onSuccess} className="w-full btn btn-primary">
          Done
        </button>
      </div>
    );
  }

  // Form state
  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <FormField label="Email address" id="invite-email" required>
        <input
          type="email"
          id="invite-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input"
          placeholder="family@example.com"
          required
        />
      </FormField>

      <FormField label="Role" id="invite-role" required>
        <div className="space-y-3">
          <label className="flex items-start p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="role"
              value="OWNER"
              checked={role === 'OWNER'}
              onChange={(e) => setRole(e.target.value as FamilyRole)}
              className="mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Owner</div>
              <div className="text-sm text-gray-600">
                Full access, can invite/remove members
              </div>
            </div>
          </label>

          <label className="flex items-start p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="role"
              value="MEMBER"
              checked={role === 'MEMBER'}
              onChange={(e) => setRole(e.target.value as FamilyRole)}
              className="mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Member</div>
              <div className="text-sm text-gray-600">
                Can edit tasks, medications, and appointments
              </div>
            </div>
          </label>

          <label className="flex items-start p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="role"
              value="VIEWER"
              checked={role === 'VIEWER'}
              onChange={(e) => setRole(e.target.value as FamilyRole)}
              className="mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Viewer</div>
              <div className="text-sm text-gray-600">Read-only access</div>
            </div>
          </label>
        </div>
      </FormField>

      <div className="flex gap-3 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create invite'}
        </button>
      </div>
    </form>
  );
}
